import React, { useState, useEffect, useCallback, useRef } from "react";
import { usePagination } from "../hooks";
import {
  Box,
  Button,
  IconButton,
  InputBase,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  RestartAlt as ResetIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { SCREEN_IDS } from "../components/FullComponent.js";
import { typeConnect, backgroundColor2 } from "../constants/Constants";
import { translateText } from "../translations/translationService";

import {
  getFullMoForMachining,
  insertNewTiempoMachining,
  updateTiempoMezanizado,
  updateMachiningLineVisibility,
} from "../services/ConnectDataBase";
import { getCurrentDateTime, formatDate } from "../services/Functions";
import { updateListaPesos } from "../redux/reducers";
import { BRAND_COLORS, BORDER_COLORS, STATUS_COLORS, BACKGROUND_COLORS, MACHINING_SCREEN_COLORS } from "../style/colors.js";

export default function MachiningScreen() {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const lang = useSelector((state) => state.lang.value);
  const [openResetModal, setOpenResetModal] = useState(false);
  const [resetMoCode, setResetMoCode] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const screenName = "MachiningScreen";
  const currentScreenId = SCREEN_IDS.MACHINING_SCREEN;
  const tipo_tiempo = "TIME_WORK";
  const listData = useSelector((state) => state.pesosList.listaPesos);
  const usuario = useSelector((state) => state.user.name);
  const equipo = useSelector((state) => state.user.team);
  const [refSearch, setRefSearch] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [weights, setWeights] = useState({});
  // Hook de paginación
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(0, 25);

  const getMo = useCallback(async () => {
    setIsLoading(true);
    try {
      const fullMo = await getFullMoForMachining(
        screenName,
        typeConnect[0],
        currentScreenId
      );
      const moArray = Object.entries(fullMo).map(([po, mo], index) => ({
        key: po,
        po,
        ...mo,
      }));
      setIsLoading(false);
      dispatch(updateListaPesos(moArray));

      const initialWeights = moArray.reduce((acc, item) => {
        acc[item.key] = "";
        return acc;
      }, {});
      setWeights(initialWeights);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  }, [dispatch, screenName, currentScreenId]);

  useEffect(() => {
    getMo();
  }, [getMo]);

  const handleWeightChange = (mo, weight) => {
    if (/^\d+(\.\d*)?$/.test(weight) || weight === "") {
      setWeights((prevWeights) => ({
        ...prevWeights,
        [mo]: weight,
      }));
    }
  };

  const handleSearch = () => {
    const trimmedRefSearch = refSearch.trim();
    if (trimmedRefSearch === "") {
      setSearchResult(null);
      return;
    }
    const result = listData.find(
      (item) => item.mo === trimmedRefSearch.replace("'", "-")
    );
    setSearchResult(result ? [result] : []);
    setRefSearch("");
  };

  const handleAddEntrada = async (item) => {
    const status = await insertNewTiempoMachining(
      item.po,
      item.mo,
      usuario,
      equipo,
      1,
      getCurrentDateTime(),
      "Mecanizado.",
      tipo_tiempo,
      currentScreenId,
      item.cantidad
    );
    if (status) {
      await getMo();
      console.log("Insert new machining time.");
      setSearchResult(null);
      setRefSearch("");
    }
  };

  const handleAddSalida = async (item) => {
    const newWeight = weights[item.key];
    if (newWeight && parseFloat(newWeight) !== 0) {
      const status = await updateTiempoMezanizado(
        item.po,
        item.mo,
        2,
        getCurrentDateTime(),
        newWeight,
        "Mecanizado.",
        currentScreenId
      );
      if (status) {
        await getMo();
        console.log("Update weight value.");
        setSearchResult(null);
        setRefSearch("");
      }
    } else {
      console.log("Invalid weight value.");
    }
  };

  const handleDeleteItem = async (item) => {
    const status = await updateMachiningLineVisibility(
      item.po,
      item.mo,
      2,
      "MecanizadoEliminado",
      currentScreenId
    );
    if (status) {
      await getMo();
      console.log("Delete machining time.");
      setSearchResult(null);
      setRefSearch("");
    }
  };


  const handleOpenResetModal = () => {
    setOpenResetModal(true);
    setResetMoCode("");
  };

  const handleCloseResetModal = () => {
    setOpenResetModal(false);
    setResetMoCode("");
  };

  const resetMachiningWeight = async (moCode) => {
    try {
      const status = await updateMachiningLineVisibility(
        null,
        moCode,
        2,
        "PesoReseteado",
        currentScreenId
      );

      return status;
    } catch (error) {
      console.error("Error en resetMachiningWeight:", error);
      return false;
    }
  };

  const handleResetConfirm = async () => {
    const trimmedMoCode = resetMoCode.trim();
    if (trimmedMoCode === "") {
      console.log("Debe ingresar un código de MO");
      return;
    }

    try {
      const status = await resetMachiningWeight(trimmedMoCode, currentScreenId);

      if (status) {
        console.log(`Peso de MO ${trimmedMoCode} reseteado exitosamente`);
        await getMo();
        handleCloseResetModal();
      } else {
        console.log("Error al resetear el peso de la MO");
      }
    } catch (error) {
      console.error("Error al resetear MO:", error);
    }
  };

  const renderList = () => {
    const dataToRender =
      searchResult && searchResult.length > 0 ? searchResult : listData;

    if (dataToRender.length === 0) {
      return (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
            textAlign: "center",
            py: 8,
          }}>
          <Typography variant='h6' sx={{ color: MACHINING_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280" }}>
            {translateText("No hay datos disponibles.", lang)}
          </Typography>
        </Paper>
      );
    }

    const paginatedData = dataToRender.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <Paper
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
        }}>
        <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
          <Table stickyHeader size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("PO", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("MO", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Cantidad", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Referencia", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Entrega", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Stamping", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                  }}>
                  {translateText("Acciones", lang)}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 600,
                    backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                    borderBottom: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                    fontSize: "0.75rem",
                    color: "black",
                    py: 1,
                    px: 1.5,
                    width: 50,
                  }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((item, index) => {
                const hasFechaIni = item.fecha_ini !== null;
                const moWeight = weights[item.key] || "";
                const calculatedPlaceholder = translateText("Peso", lang);
                return (
                  <TableRow
                    key={item.key}
                    sx={{
                      "&:hover": {
                        backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
                      },
                      borderBottom: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                    }}>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: MACHINING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {item.po}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: MACHINING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {item.mo}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: MACHINING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {Math.round(item.cantidad)}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: MACHINING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {item.referencia}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: MACHINING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {formatDate(item.fechaentrega)}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontSize: "0.8rem",
                        py: 0.75,
                        px: 1.5,
                        color: MACHINING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                      }}>
                      {item.stamping}
                    </TableCell>
                    <TableCell align='right' sx={{ py: 0.75, px: 1.5 }}>
                      {!hasFechaIni ? (
                        <Button
                          onClick={() => handleAddEntrada(item)}
                          variant='contained'
                          size='small'
                          sx={{
                            backgroundColor: BRAND_COLORS.PRIMARY,
                            color: "white",
                            textTransform: "none",
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            minWidth: "80px",
                            height: "28px",
                            padding: "4px 12px",
                            boxShadow: "none",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: BRAND_COLORS.PRIMARY_DARK,
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 4px rgba(49, 79, 91, 0.2)",
                            },
                          }}>
                          {translateText("Entrada", lang)}
                        </Button>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}>
                          <TextField
                            value={moWeight}
                            onChange={(e) =>
                              handleWeightChange(item.key, e.target.value)
                            }
                            type='number'
                            placeholder={calculatedPlaceholder}
                            size='small'
                            sx={{
                              width: "100px",
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 1,
                                fontSize: "0.75rem",
                                backgroundColor: "white",
                                height: "28px",
                                "& fieldset": {
                                  borderColor: MACHINING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb",
                                },
                                "&:hover fieldset": {
                                  borderColor: BRAND_COLORS.PRIMARY,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: BRAND_COLORS.PRIMARY,
                                  borderWidth: "1px",
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                py: 0.5,
                                textAlign: "center",
                              },
                            }}
                          />
                          <Button
                            onClick={() => handleAddSalida(item)}
                            variant='contained'
                            size='small'
                            sx={{
                              backgroundColor: BRAND_COLORS.PRIMARY,
                              color: "white",
                              textTransform: "none",
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              minWidth: "80px",
                              height: "28px",
                              padding: "4px 12px",
                              boxShadow: "none",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: BRAND_COLORS.PRIMARY_DARK,
                                transform: "translateY(-1px)",
                                boxShadow: "0 2px 4px rgba(49, 79, 91, 0.2)",
                              },
                            }}>
                            {translateText("Salida", lang)}
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                      <IconButton
                        onClick={() => handleDeleteItem(item)}
                        size='small'
                        sx={{
                          color: MACHINING_SCREEN_COLORS.TEXT_SECONDARY,
                          padding: 0.5,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: BACKGROUND_COLORS.SECONDARY,
                            color: STATUS_COLORS.ERROR,
                            transform: "scale(1.1)",
                          },
                        }}>
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />

        <Box sx={{ backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB" }}>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component='div'
            count={dataToRender.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-toolbar": {
                fontSize: "0.75rem",
                color: MACHINING_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280",
                minHeight: "52px",
              },
              "& .MuiTablePagination-select": {
                fontSize: "0.75rem",
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.75rem",
              },
            }}
          />
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ padding: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
        }}>
        <Box sx={{ flex: 1, minWidth: "280px", position: "relative" }}>
          <InputBase
            inputRef={inputRef}
            value={refSearch}
            onChange={(e) => setRefSearch(e.target.value)}
            placeholder={translateText("Código de MO", lang)}
            sx={{
              width: "100%",
              backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_CARD || "#F9FAFB",
              color: MACHINING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: "8px 12px 8px 40px",
              borderRadius: 2,
              border: `1.5px solid ${MACHINING_SCREEN_COLORS.BORDER_CARD || "#e5e7eb"}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_INPUT || "#F3F4F6",
                borderColor: MACHINING_SCREEN_COLORS.BORDER_BRAND || "#174045",
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                borderColor: MACHINING_SCREEN_COLORS.BORDER_BRAND || "#174045",
                boxShadow:
                  "0 0 0 2px rgba(70, 93, 100, 0.1), 0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "& input": {
                padding: 0,
              },
              "& input::placeholder": {
                color: MACHINING_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
                opacity: 1,
              },
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: MACHINING_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        <Button
          onClick={handleSearch}
          variant='contained'
          size="small"
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY || "#174045",
            color: "white",
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY || "#1e4d52",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Buscar", lang)}
        </Button>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: MACHINING_SCREEN_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <Typography variant='h6' sx={{ flex: 1, textAlign: "center", fontSize: "1rem", fontWeight: 600 }}>
          {translateText("Tabla de MO", lang)}
        </Typography>

        <IconButton
          onClick={handleOpenResetModal}
          sx={{
            padding: 1,
            color: MACHINING_SCREEN_COLORS.TEXT_BRAND || "#174045",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(70, 93, 100, 0.08)",
              transform: "scale(1.1)",
            },
          }}>
          <ResetIcon />
        </IconButton>
      </Box>

      {/* Modal de Reset */}
      <Dialog
        open={openResetModal}
        onClose={handleCloseResetModal}
        maxWidth='sm'
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}>
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 600,
            color: MACHINING_SCREEN_COLORS.TEXT_BRAND_DARK,
            fontSize: "1.2rem",
            pb: 1,
          }}>
          {translateText("Resetear Peso de MO", lang)}
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography
            variant='body1'
            sx={{
              textAlign: "center",
              mb: 3,
              color: MACHINING_SCREEN_COLORS.TEXT_SECONDARY,
              fontSize: "0.95rem",
            }}>
            {translateText(
              "Esta acción eliminará el peso registrado para la MO especificada, permitiendo que vuelva a aparecer en la lista. Ingrese el código de MO:",
              lang
            )}
          </Typography>

          <TextField
            fullWidth
            value={resetMoCode}
            onChange={(e) => setResetMoCode(e.target.value)}
            placeholder={translateText("Código de MO", lang)}
            variant='outlined'
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                fontSize: "0.9rem",
                backgroundColor: "white",
                "& fieldset": {
                  borderColor: MACHINING_SCREEN_COLORS.BORDER_INPUT,
                },
                "&:hover fieldset": {
                  borderColor: MACHINING_SCREEN_COLORS.BORDER_BRAND_DARK,
                },
                "&.Mui-focused fieldset": {
                  borderColor: MACHINING_SCREEN_COLORS.BORDER_BRAND_DARK,
                  borderWidth: "1px",
                },
              },
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleResetConfirm();
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseResetModal}
            variant='outlined'
            sx={{
              flex: 1,
              borderColor: MACHINING_SCREEN_COLORS.BORDER_INPUT,
              color: MACHINING_SCREEN_COLORS.TEXT_SECONDARY,
              textTransform: "none",
              borderRadius: 1.5,
              fontSize: "0.9rem",
              fontWeight: 500,
              height: "42px",
              "&:hover": {
                borderColor: MACHINING_SCREEN_COLORS.BORDER_NEUTRAL,
                backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_BUTTON_DISABLED,
              },
            }}>
            {translateText("Cancelar", lang)}
          </Button>

          <Button
            onClick={handleResetConfirm}
            variant='contained'
            disabled={resetMoCode.trim() === ""}
            sx={{
              flex: 1,
              backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY,
              color: "white",
              textTransform: "none",
              borderRadius: 1.5,
              fontSize: "0.9rem",
              fontWeight: 500,
              height: "42px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY,
                boxShadow: "0 2px 4px rgba(220, 38, 38, 0.3)",
              },
              "&:disabled": {
                backgroundColor: MACHINING_SCREEN_COLORS.BACKGROUND_BUTTON_NEUTRAL,
                color: MACHINING_SCREEN_COLORS.TEXT_MUTED,
              },
            }}>
            {translateText("Resetear MO", lang)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Content Section */}
      {isLoading ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${MACHINING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      translateText("PO", lang),
                      translateText("MO", lang),
                      translateText("Cantidad", lang),
                      translateText("Referencia", lang),
                      translateText("Entrega", lang),
                      translateText("Stamping", lang),
                      translateText("Acciones", lang),
                      "",
                    ].map((header, i) => (
                      <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(8)].map((_, j) => (
                        <TableCell key={j} sx={{ py: 0.75, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      ) : (
        renderList()
      )}
    </Box>
  );
}
